import React from 'react'

import { useNavigate } from 'react-router'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { Paragraph, Button, Link } from '@client/components'
import { Spacer } from '@client/components/atoms'
import { ModalActionContainer, ModalContainerStyle } from '@client/components/Theme'
import { DeviceContainer } from '@client/components/utility'
import { useConfig } from '@client/contexts/ConfigProvider'
import {
  ACTIVATE_NO_PLAN_MESSAGE,
  ACTIVATE_RECOVERY_PATH,
  logActivateBlockedMissingId,
  logActivateRequestSent,
  resolveActivateSubscriptionId,
} from '@client/lib/ActivateSubscriptionHelper'
import { useActivateSubscription, useUserCartQuery, useUserDetailsQuery } from '@hooks/api'
import type { RegisteredUserDetailsFragment, UserDetailsFragment } from '@hooks/api'
import { Modal } from '@molecules/index'

const Container = styled.div`
  ${ModalContainerStyle}
`
const ActionContainer = styled.div`
  ${ModalActionContainer()}
`

export interface ReactivateSubscriptionModalProps {
  open?: boolean
  onClose: (subscriptionPaused?: boolean) => void
  onReactivation: () => void
}

export function ReactivateSubscriptionModal({ open, onClose, onReactivation }: ReactivateSubscriptionModalProps): JSX.Element {

  const config = useConfig()
  const navigate = useNavigate()
  const { addToast } = useToasts()

  const { data: userCartData, loading: userCartLoading } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const [activateSubscription, { loading: activateLoading }] = useActivateSubscription()
  const checkoutStatus = userCartData?.currentUser?.checkoutStatus
  const registeredUser = userDetailsData?.currentUser as (UserDetailsFragment & RegisteredUserDetailsFragment) | undefined

  const _handleReactivate = async (): Promise<void> => {
    if (checkoutStatus?.shouldPresentCheckoutUI && typeof window !== 'undefined') {
      window.location.href = ACTIVATE_RECOVERY_PATH
      return
    }

    const subscriptionId = resolveActivateSubscriptionId({
      activeMenuSubscriptionId: userCartData?.currentUser?.activeMenu?.subscription?.id ?? undefined,
      subscriptions: registeredUser?.subscriptions?.map((s) => ({ id: s.id, status: s.status })) ?? undefined,
    })

    if (!subscriptionId) {
      logActivateBlockedMissingId('ReactivateSubscriptionModal')
      addToast(ACTIVATE_NO_PLAN_MESSAGE, { appearance: 'error', autoDismiss: true })
      if (typeof window !== 'undefined') {
        window.location.href = ACTIVATE_RECOVERY_PATH
      }
      return
    }

    try {
      logActivateRequestSent('ReactivateSubscriptionModal', true)
      await activateSubscription({
        variables: {
          id: subscriptionId,
          reset: !userCartData?.currentUser?.checkoutStatus?.hasConfiguredBox,
        },
      })
      onReactivation()
      addToast('Your plan is active!', {
        appearance: 'success',
        autoDismiss: true,
      })
    } catch (e) {
      addToast((e as Error).message, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const _handleClose = (): void => {
    onClose()
  }

  const loading = userCartLoading || activateLoading

  return (
    <Modal
      open={open}
      onClose={_handleClose}
      allowBackgroundClose={false}
      fullscreen={false}
      title='Activate your subscription'>
      <Container>
        <DeviceContainer $mobile $tablet>
          <Paragraph variant='p1'>
            Your Meal Kit plan is currently paused. To add dishes to your next order, activate your plan by selecting activate below.
          </Paragraph>
        </DeviceContainer>
        <DeviceContainer $desktop>
          <Paragraph variant='p1' align='center'>
            Your Meal Kit plan is currently paused. To add dishes to your next order, activate your plan by selecting activate below.
          </Paragraph>
        </DeviceContainer>
        <Spacer universal='24px' />
        <ActionContainer>
          <DeviceContainer $mobile $tablet className='full-width'>
            <Button
              loading={loading}
              color='black'
              fullWidth
              title='ACTIVATE NOW'
              onClick={_handleReactivate} />
          </DeviceContainer>
          <DeviceContainer $desktop>
            <Button
              loading={loading}
              color='black'
              fullWidth
              title='ACTIVATE NOW'
              onClick={_handleReactivate} />
          </DeviceContainer>
          <Spacer mobile='8px' desktop='16px' />
          <Link variant='l2' decoration='underline' onClick={_handleClose}> No thanks </Link>
        </ActionContainer>
      </Container>
    </Modal>
  )
}
